import React, { Fragment, useContext, useEffect, useMemo } from 'react';
import { bisect, merge } from 'd3-array';
import { timeDay, timeMinute } from 'd3-time';
import { useTranslation } from 'react-i18next';

import AGPReport from './AGPReport';
import AGPSettingsContext from './AGPSettingsContext';
import Cycles from './Cycles';
import DailyView from './DailyView';
import DayFilterContext from '../DayFilterContext';
import Days from './Days';
import DiaryView from './DiaryView';
import ErrorBoundary from '../ErrorBoundary';
import ExerciseReport from './ExerciseReport';
import Footer from './Footer';
import GlucoseStatsRow from './GlucoseStatsRow';
import Header from './Header';
import InsulinReport from './InsulinReport';
import InsulinSettingContext from './InsulinSettingsContext';
import InsulinStatsRow from './InsulinStatsRow';
import NutritionReport from './NutritionReport';
import NutritionSettingContext from './NutritionSettingsContext';
import NutritionStatsRow from './NutritionStatsRow';
import PresentationContext from '../PresentationContext';
import { getView } from '../Routes';
import Stats from './Stats';
import { getProfilesData, timeSort } from '../utils/dataUtils';
import { parseDate, urlDateFormat } from '../utils/formats';
import { getTimeScaleForDays, getGlucoseLineForDays } from '../utils/svgUtils';
import './Dashboard.css';

const debug = (process.env.NODE_ENV !== 'production') && false;

const maxMealSlotDuration = 3; // hours

function selectDataForDays(dataOfType, days) {
  if (!dataOfType) {
    return {};
  }
  return days.reduce((o, d) => {
    o[d] = dataOfType[d] || [];
    return o;
  }, {});
}

function getMealSlot(date) {
  if (! date || !date.getHours) {
    return undefined;
  }
  return date.getHours() + (date.getMinutes() >= 30 ? 0.5 : 0);
}

function identifyMealBoluses(bolusData, nutritionData) {
  if (debug) {
    console.log('Identifying meal boluses', { bolusData, nutritionData });
  }
  const nutritionTimes = merge(Object.values(nutritionData || {})).map(v => v.time).sort();
  const future = timeDay.offset(new Date(), 1);
  merge(Object.values(bolusData)).forEach((v) => {
    const nearestIndex = bisect(nutritionTimes, v.time);
    const previousMeal = new Date(nearestIndex > 0 ? nutritionTimes[nearestIndex - 1] : 0);
    const nextMeal = new Date(nearestIndex < nutritionTimes.length ? nutritionTimes[nearestIndex] : future);
    const bolusDate = new Date(v.time);
    if ((timeMinute.count(previousMeal, bolusDate) < 60) || (timeMinute.count(bolusDate, nextMeal) < 60)) {
      if (debug) {
        console.log('Identified meal bolus', { v, previousMeal, nextMeal }, timeMinute.count(previousMeal, bolusDate), timeMinute.count(bolusDate, nextMeal));
      }
      v.mealBolus = true;
    }
  });
}

function Dashboard(props) {
  const {
    history,
    interactive = true,
    path = history ? history.location.pathname : document.location.pathname,
    store,
  } = props;
  const { t } = useTranslation('dashboard');
  const { activeDays, day, days } = useContext(DayFilterContext);
  const {
    formatDateRange,
    formatGlucoseValue,
  } = useContext(PresentationContext);
  const { percentile } = useContext(AGPSettingsContext);
  const { mealThreshold, setMealTimes, setMealThreshold } = useContext(NutritionSettingContext);
  const { bolusFocus, selectBolusFocus } = useContext(InsulinSettingContext);

  const timeScale = useMemo(() => getTimeScaleForDays({ days: days.map(parseDate) }), [days]);
  const glucoseLineGenerator = useMemo(() => getGlucoseLineForDays({ days, timeScale }), [days, timeScale]);
  const { data = {}, session = {} } = store;
  const { profiles } = data;
  const { user } = session;
  const {
    basal, bolus, pumpEvents, exercises, longActing, nutrition, notes, photos, sgv, sleep, smbg, steps
  } = data;

  const activatedDays = useMemo(() => Object.keys(activeDays).length
  ? days.filter(d => activeDays[d])
  : days, [activeDays, days]);

  const basalData = useMemo(() => {
    const previousDate = timeDay.offset(parseDate(days[0]), -1);
    // Check whether that date should be activated
    // We'd need the activated day names for this...
    // Would be better to check applicability where the data is used...
    const previousDay = urlDateFormat(previousDate);
    const nextDay = urlDateFormat(timeDay.offset(parseDate(days[days.length - 1]), 1));
    const selectedBasal = selectDataForDays(basal, [ previousDay, ...days, nextDay ]);
    return selectedBasal;
  }, [basal, days]);
  const longActingData = useMemo(() => {
    const previousDate = timeDay.offset(parseDate(days[0]), -1);
    // Check whether that date should be activated (same as with basal)
    const previousDay = urlDateFormat(previousDate);
    const nextDay = urlDateFormat(timeDay.offset(parseDate(days[days.length - 1]), 1));
    const selectedLong = selectDataForDays(longActing, [ previousDay, ...days, nextDay ]);
    return selectedLong;
  }, [longActing, days]);
  // const bolusData = useMemo(() => selectDataForDays(bolus, activatedDays), [activatedDays, bolus]);
  const nutritionData = useMemo(() => selectDataForDays(nutrition, activatedDays), [activatedDays, nutrition]);
  const bolusData = useMemo(() => {
    const boluses = selectDataForDays(bolus, activatedDays);
    identifyMealBoluses(boluses, nutritionData);
    return boluses;
  }, [activatedDays, bolus, nutritionData]);
  const eventData = useMemo(() => selectDataForDays(pumpEvents, activatedDays), [activatedDays, pumpEvents]);
  const exerciseData = useMemo(() => selectDataForDays(exercises, activatedDays), [activatedDays, exercises]);
  const profilesData = useMemo(() => getProfilesData(days, basal, profiles), [days, basal, profiles]);
  const notesData = useMemo(() => selectDataForDays(notes, activatedDays), [activatedDays, notes]);
  const photosData = useMemo(() => selectDataForDays(photos, activatedDays), [activatedDays, photos]);
  const sgvData = useMemo(() => selectDataForDays(sgv, activatedDays), [activatedDays, sgv]);
  const sleepData = useMemo(() => {
    const previousDay = urlDateFormat(timeDay.offset(parseDate(days[0]), -1));
    return selectDataForDays(sleep, [ previousDay, ...activatedDays ]);
  }, [activatedDays, days, sleep]);
  const smbgData = useMemo(() => selectDataForDays(smbg, activatedDays), [activatedDays, smbg]);
  const stepsData = useMemo(() => selectDataForDays(steps, activatedDays), [activatedDays, steps]);

  const glucosePath = useMemo(() => glucoseLineGenerator(merge(Object.values(sgvData)).sort(timeSort)), [sgvData, glucoseLineGenerator]);

  const view = getView(path);
  
  useEffect(() => {
    if (view !== 'nutrition') {
      // Don't bother calculating this for other views
      return;
    }

    const dates = merge(Object.values(nutritionData) || [])
    .filter(v => v.carbohydrate > mealThreshold)
    .map(v => new Date(v.time));
    const mealsInSlots = dates.reduce((o, d) => {
      const mealTime = getMealSlot(d);
      if (mealTime) {
        if (!o[mealTime]) {
          const arr = [];
          for(let i = 0; i < maxMealSlotDuration * 2; i += 1) {
            arr[i] = {};
          }
          o[mealTime] = arr;
        }
        o[mealTime][0][urlDateFormat(d)] = 1;
      }
      for (let i = 1; i < maxMealSlotDuration * 2; i += 1) {
        const previousMealTime = mealTime - (i * 0.5);
        if (o[previousMealTime]) {
          o[previousMealTime][i][urlDateFormat(d)] = 1;
        }
      }
      return o;
    }, {});

    let sorted = Object.keys(mealsInSlots).map((k) => {
      const dates = mealsInSlots[k].reduce((o, m, i) => {
        Object.keys(m).forEach((d) => {
          o[d] = 5 - i; // Weighted, prefer the actual slot
        });
        return o;
      }, {});
      return { slot: k, dateCount: Object.values(dates).reduce((v, n) => v + n, 0) };
    });

    sorted.sort((a,b) => b.dateCount - a.dateCount);

    for (let index = 0; index < 5 && index < sorted.length; index += 1) {
      const t = +sorted[index].slot;
      sorted = sorted.filter((s, i) => i <= index || (+s.slot < (t - maxMealSlotDuration)) || (+s.slot > (t + maxMealSlotDuration)));
    }
    const mealTimes = sorted.map(s => ({ start: +s.slot, end: Math.min(+s.slot + maxMealSlotDuration, 24) })).sort((a,b) => a.start - b.start);
    setMealTimes(mealTimes);
  }, [mealThreshold, nutritionData, setMealTimes, view]);

  /*
  if (!days) {
    console.error('No days!');
  }
  if (!merge(Object.values(nutritionData)).length) {
    console.error('No nutrition data!', { nutrition, days, activatedDays, activeDays, data }, Object.keys(nutrition));
  }
  */

  let overview = (
    <Fragment>
      <Stats key="stats" className="stats" />
      <Stats key="overview" className="overview" />
    </Fragment>
  );
  let mainView;

  switch(view) {
    case 'agp':
      overview = <GlucoseStatsRow sgvData={sgvData} interactive={interactive} t={t} />;
      mainView = <AGPReport
        days={days}
        interactive={interactive}
        percentile={percentile}
        sgvData={sgvData}
        smbgData={smbgData}
        t={t}
        timeScale={timeScale}
      />;
      break;
    case 'insulin':
      overview = <InsulinStatsRow
        basal={basalData}
        bolus={bolusData}
        days={days}
        interactive={interactive}
        longActing={longActingData}
        profiles={profiles}
        profilesData={profilesData}
        t={t}
      />;
      mainView = InsulinReport({
        days,
        basalData,
        bolusData,
        bolusFocus,
        formatDateRange,
        formatGlucoseValue,
        interactive,
        nutritionData,
        percentile,
        profiles, 
        selectBolusFocus,
        sgvData,
        profilesData,
        t,
      });
      break;
    case 'nutrition':
      overview = NutritionStatsRow({
        days,
        interactive,
        mealThreshold,
        nutritionData,
        setMealThreshold,
        t,
      });
      mainView = <NutritionReport
        days={days}
        formatDateRange={formatDateRange}
        interactive={interactive}
        nutritionData={nutritionData}
        percentile={percentile}
        photosData={photosData}
        sgvData={sgvData}
        t={t}
      />
      break;
    case 'exercise':
      const exerciseProps = {
        basalData: store.data.basal,
        bolusData: store.data.bolus,
        exerciseData: store.data.exercises,
        history,
        interactive,
        notesData: store.data.notes,
        nutritionData: store.data.nutrition,
        sgvData: store.data.sgv,
        stepsData: store.data.steps,
        t,
      };
      mainView = <ExerciseReport { ...exerciseProps } />;
      break;
    case 'day':
      mainView = <DailyView
        basalData={basalData}
        bolusData={bolusData}
        day={day}
        days={days}
        eventData={eventData}
        exerciseData={exerciseData}
        interactive={interactive}
        longActingData={longActingData}
        notesData={notesData}
        nutritionData={nutritionData}
        photosData={photosData}
        sgvData={sgvData}
        sleepData={sleepData}
        smbgData={smbgData}
        stepsData={stepsData}
        t={t}
        timeScale={timeScale}
      />;
      break;
    case 'diary':
      mainView = <DiaryView
        basalData={basalData}
        bolusData={bolusData}
        day={day}
        days={days}
        eventData={eventData}
        exerciseData={exerciseData}
        interactive={interactive}
        longActingData={longActingData}
        notesData={notesData}
        nutritionData={nutritionData}
        photosData={photosData}
        profilesData={profilesData}
        sgvData={sgvData}
        sleepData={sleepData}
        smbgData={smbgData}
        stepsData={stepsData}
        t={t}
        timeScale={timeScale}
      />;
      break;
    case 'cycles':
      mainView = <Cycles
        basalData={store.data.basal}
        bolusData={store.data.bolus}
        exerciseData={store.data.exercises}
        formatDateRange={formatDateRange}
        history={history}
        interactive={interactive}
        longActingData={store.data.long}
        notesData={store.data.notes}
        nutritionData={store.data.nutrition}
        percentile={percentile}
        sgvData={store.data.sgv}
        sleepData={store.data.sleep}
        stepsData={store.data.steps}
        t={t}
      />;
      break;
    default:
  }

  return (
    <ErrorBoundary>
      <article
        className={`dashboard${
          document.location.pathname.indexOf('/reports/') > 0 ? ' example' : ''
        }`}
      >
        <Header location={path} interactive={interactive} user={user} />
        {overview}
        {mainView}
        { (view !== 'exercise' && view !== 'diary' && (view !== 'cycles'))
          ? (
            <Days
              interactive={navigator.userAgent !== 'ReactSnap'}
              timeScale={timeScale}
              data={sgvData}
              glucosePath={glucosePath}
              t={t}
            />
          )
          : undefined
        }
        <Footer />
      </article>
    </ErrorBoundary>
  );
}

export default Dashboard;
